import { useRelayEnvironment } from "react-relay";

import type { RecordProxy, RecordSourceSelectorProxy } from "relay-runtime";

/**
 * A helper function for adding a certain number to a client-side total count.
 * Needed because pagination at Skydio relies on Relay-cached totalCount field,
 * and mutations editing such totalCount fields would otherwise require a network-only
 * refresh to display accurate totalCount values (i.e., during Rivet pagination).
 * @param n - the number to add to the Relay-cached total count.
 * @param connections - array of relevant connection IDs to operation being performed.
 * @param store - object of type RecordSourceSelectorProxy needed to directly access Relay store
 * (reference: https://relay.dev/docs/api-reference/store/#recordsourceselectorproxy)
 */
export function addNToRelayTotalCount<T>(
  n: number,
  connections: string[],
  store: RecordSourceSelectorProxy<T>
) {
  const [connectionsId] = connections;
  const storeValue = store.get(connectionsId);
  const currentTotalCount = (storeValue?.getValue("totalCount") as number) ?? 0;
  storeValue?.setValue(currentTotalCount + n, "totalCount");
}

/**
 * A helper function in Relay similar to `useConnectionNodes` for graphql queries.
 * Gets an array of linked node records from a Relay "connection" record.
 */
export function getAllNodesFromConnectionField(record?: RecordProxy | null) {
  const connectionEdges = record?.getLinkedRecords("edges");
  return connectionEdges?.map(edge => edge.getLinkedRecord("node"));
}

export function getUuidFromGlobalId(globalId: string) {
  return Buffer.from(globalId, "base64").toString("utf-8").split(":")[1];
}

/**
 * Exposes a set of helper functions to fetch Relay nodes, both by ID and uuid.
 */
export const useFetchNode = () => {
  const environment = useRelayEnvironment();

  /**
   * Fetches a node from the Relay store by its global ID.
   * @param globalId The global ID of the node.
   * @returns The fetched node or null if not found.
   */
  const fetchNodeByGlobalId = (globalId: string): RecordProxy | null | undefined => {
    let node: RecordProxy | null | undefined = null;

    environment.commitUpdate(store => {
      node = store.get(globalId);
    });

    return node;
  };

  /**
   * Fetches a node by typename and UUID.
   * @param typename The typename of the node (e.g., "AnnotationCategory").
   * @param uuid The UUID of the db entity.
   * @returns The fetched node or null if not found.
   */
  const fetchNodeByUuid = (typename: string, uuid: string): RecordProxy | null | undefined => {
    const globalId = Buffer.from(`${typename}:${uuid}`, "utf-8").toString("base64");
    return fetchNodeByGlobalId(globalId);
  };

  /**
   * Fetches a node directly by its global ID.
   * @param id The global ID of the node.
   * @returns The fetched node or null if not found.
   */
  const fetchNodeById = (id: string): RecordProxy | null | undefined => {
    return fetchNodeByGlobalId(id);
  };

  return { fetchNodeByUuid, fetchNodeById };
};
